/**
 * QMS Pending Attachments Component
 * Displays files selected for upload during ticket creation (before ticket exists)
 * Supports drag-and-drop and click-to-select
 */

import { useRef, useCallback, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/utils';
import {
  Paperclip,
  Upload,
  X,
  FileText,
  Image,
  File,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import type { QmsPendingFile } from '../../types/qms.types';

interface QmsPendingAttachmentsProps {
  pendingFiles: QmsPendingFile[];
  onAddFiles: (files: FileList | File[]) => void;
  onRemoveFile: (id: string) => void;
  onClearAll: () => void;
  disabled?: boolean;
  isUploading?: boolean;
}


function isImageMimeType(mimeType?: string): boolean {
  return !!mimeType?.startsWith('image/');
}

function getFileIcon(mimeType?: string) {
  if (isImageMimeType(mimeType)) {
    return <Image className="h-4 w-4 text-blue-500" />;
  }
  if (mimeType?.includes('pdf')) {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  return <File className="h-4 w-4 text-gray-500" />;
}

function getStatusIcon(status: QmsPendingFile['status']) {
  switch (status) {
    case 'uploading':
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    default:
      return null;
  }
}

/**
 * Individual pending file item
 */
function PendingFileItem({
  file,
  onRemove,
  disabled,
}: {
  file: QmsPendingFile;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('qms');
  const isImage = isImageMimeType(file.type);
  const statusIcon = getStatusIcon(file.status);
  const isInProgress = file.status === 'uploading';

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border bg-white transition-colors ${
        file.status === 'error' ? 'border-red-200 bg-red-50' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Thumbnail or Icon */}
        {isImage && file.preview ? (
          <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden border bg-gray-100">
            <img
              src={file.preview}
              alt={file.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 w-10 h-10 rounded border bg-gray-50 flex items-center justify-center">
            {getFileIcon(file.type)}
          </div>
        )}

        {/* File info */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{file.name}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{formatBytes(file.size)}</span>
            {file.status !== 'pending' && (
              <>
                <span>•</span>
                <span
                  className={
                    file.status === 'error'
                      ? 'text-red-600'
                      : file.status === 'success'
                        ? 'text-green-600'
                        : 'text-blue-600'
                  }
                >
                  {t(`attachments.pending.status.${file.status}`)}
                </span>
              </>
            )}
            {file.error && (
              <span className="text-red-600 truncate" title={file.error}>
                - {file.error}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Status and Actions */}
      <div className="flex items-center gap-2 ml-2">
        {statusIcon}
        {!isInProgress && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Remove ${file.name}`}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function QmsPendingAttachments({
  pendingFiles,
  onAddFiles,
  onRemoveFile,
  onClearAll,
  disabled = false,
  isUploading = false,
}: QmsPendingAttachmentsProps) {
  const { t } = useTranslation('qms');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      onAddFiles(files);

      // Clear the input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onAddFiles]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || isUploading) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onAddFiles(files);
      }
    },
    [disabled, isUploading, onAddFiles]
  );

  const handleUploadClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const totalSize = pendingFiles.reduce((sum, f) => sum + f.size, 0);
  const hasFiles = pendingFiles.length > 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Paperclip className="h-4 w-4" />
          {t('attachments.pending.title')}
          {hasFiles && (
            <span className="text-gray-500 font-normal">
              ({pendingFiles.length} • {formatBytes(totalSize)})
            </span>
          )}
        </h4>
        {hasFiles && !isUploading && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            disabled={disabled}
            className="text-gray-500 hover:text-red-600 h-8 px-2"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            {t('attachments.pending.clear')}
          </Button>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-gray-500">
        {t('attachments.pending.description')}
      </p>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer ${
          isDragging
            ? 'border-atek-blue bg-atek-blue/5'
            : disabled || isUploading
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-atek-blue hover:bg-gray-50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleUploadClick}
      >
        <div className="flex flex-col items-center justify-center gap-2 py-2">
          {isUploading ? (
            <Loader2 className="h-8 w-8 text-atek-blue animate-spin" />
          ) : (
            <Upload
              className={`h-8 w-8 ${
                isDragging ? 'text-atek-blue' : 'text-gray-400'
              }`}
            />
          )}
          <p
            className={`text-sm ${
              isDragging ? 'text-atek-blue' : 'text-gray-600'
            }`}
          >
            {isUploading
              ? t('attachments.pending.status.uploading')
              : t('attachments.pending.dropzone')}
          </p>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
      />

      {/* File List */}
      {hasFiles && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {pendingFiles.map((file) => (
            <PendingFileItem
              key={file.id}
              file={file}
              onRemove={() => onRemoveFile(file.id)}
              disabled={disabled || isUploading}
            />
          ))}
        </div>
      )}
    </div>
  );
}
